import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import WarningAmberIcon from '@material-ui/icons/ReportProblemOutlined'
import CloseIcon from '@material-ui/icons/Close'
import { analyzeFastqNames, shortenFileName } from '../../../utils/fastqNaming'

// Compact result strip for the queued FASTQ names, checked against the same
// rules the server pairs by so a bad name is caught before the upload.
//
// Deliberately ONE fixed-height row of chips: this sits above the file list in
// a column flex box, so anything that grows with the number of problems eats
// the list's space. Detail lives behind the chips, in a dialog.
const STRIP_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  // Fixed height and no wrapping: the strip must not change size with the
  // number of issues, or the file list below it shrinks.
  height: 36,
  minHeight: 36,
  flexShrink: 0,
  flexWrap: 'nowrap',
  overflowX: 'auto',
  overflowY: 'hidden',
  position: 'relative',
  zIndex: 1,
  mb: 1,
  '&::-webkit-scrollbar': { height: 4 },
}

const SEVERITY = {
  error: { color: 'error', Icon: ErrorOutlineIcon, label: 'problem' },
  warning: { color: 'warning', Icon: WarningAmberIcon, label: 'warning' },
}

const FastqNamingCheck = ({ fileNames }) => {
  const [open, setOpen] = useState(false)
  const result = useMemo(() => analyzeFastqNames(fileNames), [fileNames])

  if (!fileNames || fileNames.length === 0) return null

  const { issues, groups, errorCount, warningCount } = result
  const errors = issues.filter((i) => i.severity === 'error')
  const warnings = issues.filter((i) => i.severity === 'warning')
  const pairs = groups.filter((g) => g.files.length === 2).length

  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`

  const renderIssueList = (list, severity) => {
    const { Icon, color } = SEVERITY[severity]
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <Icon fontSize="small" color={color} />
          <Typography variant="subtitle2">
            {plural(list.length, severity === 'error' ? 'problem' : 'warning')}
          </Typography>
        </Box>
        {list.map((issue) => (
          <Box key={`${issue.code}-${issue.files.join('|')}`} sx={{ mb: 1.5, pl: 3 }}>
            <Typography variant="body2">{issue.message}</Typography>
            {issue.files.map((name) => (
              <Tooltip key={name} title={name} arrow>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', fontFamily: 'monospace', color: 'text.secondary', cursor: 'default' }}
                >
                  {shortenFileName(name)}
                </Typography>
              </Tooltip>
            ))}
          </Box>
        ))}
      </Box>
    )
  }

  return (
    <>
      <Box sx={STRIP_SX}>
        {pairs > 0 && (
          <Chip
            size="small"
            variant="outlined"
            color="success"
            icon={<CheckCircleIcon />}
            label={plural(pairs, 'pair')}
          />
        )}
        {errorCount > 0 && (
          <Tooltip title="Show details" arrow>
            <Chip
              size="small"
              color="error"
              icon={<ErrorOutlineIcon />}
              label={plural(errorCount, 'problem')}
              onClick={() => setOpen(true)}
              clickable
            />
          </Tooltip>
        )}
        {warningCount > 0 && (
          <Tooltip title="Show details" arrow>
            <Chip
              size="small"
              color="warning"
              variant={errorCount > 0 ? 'outlined' : 'filled'}
              icon={<WarningAmberIcon />}
              label={plural(warningCount, 'warning')}
              onClick={() => setOpen(true)}
              clickable
            />
          </Tooltip>
        )}
        {issues.length === 0 && pairs > 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
            names look fine
          </Typography>
        )}
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <span>File name check</span>
          <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {errors.length > 0 && renderIssueList(errors, 'error')}
          {errors.length > 0 && warnings.length > 0 && <Divider sx={{ mb: 2 }} />}
          {warnings.length > 0 && renderIssueList(warnings, 'warning')}
          {errorCount > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Uploading is still allowed, in case the names are deliberate. Rename the files if they are not.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

FastqNamingCheck.propTypes = {
  fileNames: PropTypes.arrayOf(PropTypes.string),
}

FastqNamingCheck.defaultProps = {
  fileNames: [],
}

export default FastqNamingCheck
